import { useEffect } from 'react';

import i18n from './i18n';
import { DEFAULT_LANGUAGE, isRenderableLanguage } from './languages';

/**
 * The club's display language, applied to the app.
 *
 * There is exactly one language setting in the product and it belongs to the CLUB — an admin sets
 * it in Admin → Preferences, it is stored on the clubs row in Zero.Auth, and it arrives on
 * GET /auth/profile beside the club's brand colours. Nothing is stored in the browser: the old
 * localStorage['language'] was per browser origin rather than per user or per club, so a super
 * admin's choice followed them into every club they acted as.
 *
 * This lives beside clubBranding.ts and mirrors it deliberately, including having two call sites:
 * the player profile renders outside the staff Layout and needs the same treatment.
 */

/**
 * The language to switch to, or null if nothing should change.
 *
 * `clubLanguage` carries two meanings that must NOT be collapsed into one:
 *   - `undefined` — UNKNOWN. The profile hasn't loaded yet, or the query cache was just cleared
 *     (a super-admin club switch: `activateClubContext` calls `queryClient.clear()`, and `Layout`
 *     stays mounted across the `/super-admin` -> `/` navigation, so `profile` briefly goes
 *     `undefined` before the new club's profile arrives). This MUST apply nothing and leave
 *     whatever is already on screen — collapsing this into the `null` case was a real, shipped
 *     bug: it flipped the whole UI to English/LTR for the network round trip on every club switch,
 *     even one Hebrew club to another.
 *   - `null` — KNOWN. The club has been fetched and genuinely has no language configured, so it
 *     falls back to English.
 * `undefined` is checked first, before any normalisation, so it can never fall through to the
 * `.trim()` call below (which only ever sees a string, via `null` coalescing to `''`).
 *
 * Exported for its test. Returning null rather than the current language matters: i18next's
 * changeLanguage re-emits and re-renders even for a no-op switch.
 */
export function languageToApply(
  clubLanguage: string | null | undefined,
  current: string | undefined,
): string | null {
  if (clubLanguage === undefined) return null;
  const raw = (clubLanguage ?? '').trim().toLowerCase();
  const next = isRenderableLanguage(raw) ? raw : DEFAULT_LANGUAGE;
  return next === current ? null : next;
}

/**
 * Apply the club's language now. Awaits i18next, which awaits the locale chunk, so callers that
 * await this can guarantee the next paint is fully translated.
 */
export async function applyClubLanguage(clubLanguage: string | null | undefined): Promise<void> {
  const next = languageToApply(clubLanguage, i18n.language);
  if (next) await i18n.changeLanguage(next);
}

/** React form — re-applies whenever the club's configured language changes. */
export function useClubLanguage(clubLanguage: string | null | undefined): void {
  useEffect(() => {
    void applyClubLanguage(clubLanguage);
  }, [clubLanguage]);
}
